/**
 * 视频生成模式选择器
 * 三种模式：text2video / img2video / first_last_frame
 * 不支持的模式按 enabledModes 自动隐藏
 */

import { Type, Image as ImageIcon, Film } from 'lucide-react';
import type { VideoMode } from '../services/videoApi';

interface VideoModeSelectorProps {
  value: VideoMode;
  onChange: (value: VideoMode) => void;
  enabledModes: Record<VideoMode, boolean>;
}

interface ModeOption {
  id: VideoMode;
  label: string;
  description: string;
  icon: typeof Type;
}

const ALL_OPTIONS: ModeOption[] = [
  {
    id: 'text2video',
    label: '文生视频',
    description: '仅用提示词生成视频',
    icon: Type,
  },
  {
    id: 'img2video',
    label: '图生视频',
    description: '上传一张图片让画面动起来',
    icon: ImageIcon,
  },
  {
    id: 'first_last_frame',
    label: '首尾帧',
    description: '上传首帧和尾帧，生成过渡视频',
    icon: Film,
  },
];

export function VideoModeSelector({ value, onChange, enabledModes }: VideoModeSelectorProps) {
  const visibleOptions = ALL_OPTIONS.filter((option) => enabledModes[option.id]);

  if (visibleOptions.length === 0) {
    return (
      <div className="text-sm text-light-muted">该模型未启用任何生成模式</div>
    );
  }

  return (
    <div>
      <div className="text-sm font-medium text-light-text mb-2">生成模式</div>
      <div className="grid grid-cols-3 gap-2">
        {visibleOptions.map((option) => {
          const Icon = option.icon;
          const isActive = option.id === value;
          return (
            <button
              key={option.id}
              type="button"
              onClick={() => onChange(option.id)}
              className={`flex flex-col items-center justify-center gap-1.5 px-2 py-3 rounded-xl border text-sm transition-all ${
                isActive
                  ? 'border-accent-blue bg-accent-blue/5 text-accent-blue shadow-md'
                  : 'border-light-border bg-white text-light-secondary hover:border-accent-blue hover:text-accent-blue'
              }`}
              title={option.description}
            >
              <Icon className="w-5 h-5" />
              <span className="font-medium leading-tight">{option.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
